// pages/UserView.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, User, Smartphone, Mail, Phone, Wifi, WifiOff,
    Loader2, Calendar, Clock, CheckCircle, XCircle, MapPin,
    Shield, Monitor, Download, FileText, Video, Image, X
} from 'lucide-react';
import { getUserById } from '../Masters/AdminAPI';
import { Panel, StatusDot, timeAgo } from '../Masters/UiElements';

const InfoRow = ({ icon: Icon, label, value, className = '' }) => (
    <div className={`flex items-start gap-3 py-3 border-b border-gray-100 last:border-0 ${className}`}>
        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
            <Icon size={16} className="text-orange-500" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 font-medium">{label}</p>
            <p className="text-sm text-gray-800 break-words">{value || '—'}</p>
        </div>
    </div>
);

const PermissionBadge = ({ name, enabled }) => (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
        <span className="text-xs font-medium text-gray-600">{name}</span>
        {enabled ? (
            <CheckCircle size={14} className="text-green-500" />
        ) : (
            <XCircle size={14} className="text-red-400" />
        )}
    </div>
);

// Modal for previewing a signature image or authentication video inline,
// instead of opening it in a new browser tab.
const MediaModal = ({ media, onClose }) => {
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    if (!media) return null;

    const handleDownload = async (url, fileName = 'download') => {
        if (!url) return;

        try {
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Download failed: ${response.status}`);
            }

            const blob = await response.blob();

            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);

            // Clean up
            window.URL.revokeObjectURL(blobUrl);

        } catch (error) {
            console.error('Download error:', error);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2 min-w-0">
                        {media.type === 'image' ? (
                            <Image size={16} className="text-orange-500 flex-shrink-0" />
                        ) : (
                            <Video size={16} className="text-orange-500 flex-shrink-0" />
                        )}
                        <p className="text-sm font-semibold text-gray-800 truncate">{media.title}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                            onClick={() => handleDownload(media.url, `${media.title}`)}
                            className="p-1.5 rounded-lg hover:bg-orange-50 text-gray-500 hover:text-orange-500 transition-colors"
                            title="Download"
                        >
                            <Download size={16} />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                            title="Close"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center p-4">
                    {media.type === 'image' ? (
                        <img
                            src={media.url}
                            alt={media.title}
                            className="max-w-full max-h-[65vh] object-contain rounded-lg"
                        />
                    ) : (
                        <video
                            src={media.url}
                            controls
                            autoPlay
                            className="max-w-full max-h-[65vh] rounded-lg"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const SingleUser = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeMedia, setActiveMedia] = useState(null);

    useEffect(() => {
        const loadUser = async () => {
            try {
                setLoading(true);
                setError('');
                const response = await getUserById(id);
                if (response.success) {
                    setUser(response.user);
                } else {
                    setError(response.message || 'Failed to load user details');
                }
            } catch (err) {
                setError(err?.response?.data?.message || err.message || 'Failed to load user');
            } finally {
                setLoading(false);
            }
        };
        if (id) loadUser();
    }, [id]);

    const openMedia = useCallback((media) => setActiveMedia(media), []);
    const closeMedia = useCallback(() => setActiveMedia(null), []);

    const handleDownload = async (url, fileName) => {
        if (!url) return;

        try {
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Download failed: ${response.status}`);
            }

            const blob = await response.blob();

            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);

            // Clean up
            window.URL.revokeObjectURL(blobUrl);

        } catch (error) {
            console.error('Download error:', error);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <Loader2 size={40} className="animate-spin text-orange-500 mx-auto" />
                    <p className="mt-4 text-gray-600">Loading user details...</p>
                </div>
            </div>
        );
    }

    if (error || !user) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center max-w-md">
                    <div className="text-red-500 text-5xl mb-4">⚠️</div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">User Not Found</h3>
                    <p className="text-gray-600 mb-6">{error || 'The requested user could not be found'}</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:shadow-lg hover:shadow-orange-500/30 transition-all"
                    >
                        Back to Users
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 rounded-xl hover:bg-orange-100 transition-colors text-gray-600 hover:text-orange-600"
                    >
                        <ArrowLeft size={24} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">User Details</h1>
                        <p className="text-sm text-gray-500">View complete user and device information</p>
                    </div>
                </div>

                {/* User Profile Card */}
                <div className="bg-white rounded-2xl shadow-xl border border-orange-200 overflow-hidden">
                    {/* Header with Avatar */}
                    <div className="px-6 py-5 border-b border-orange-100 bg-gradient-to-r from-orange-50 to-amber-50">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg flex-shrink-0">
                                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <h2 className="text-xl font-bold text-gray-800">{user.name}</h2>
                                    <div className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200">
                                        {user.isOnline ? (
                                            <>
                                                <Wifi size={10} className="text-green-500" />
                                                <span className="text-green-600 font-medium">Online</span>
                                            </>
                                        ) : (
                                            <>
                                                <WifiOff size={10} className="text-gray-400" />
                                                <span className="text-gray-500">Offline</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <p className="text-sm text-gray-500 mt-0.5">{user.email}</p>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="p-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Left Column - Personal Information */}
                            <div>
                                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                    <User size={16} className="text-orange-500" />
                                    Personal Information
                                </h3>
                                <div className="bg-gray-50 rounded-xl border border-gray-200 divide-y divide-gray-100">
                                    <InfoRow icon={User} label="Full Name" value={user.name} />
                                    <InfoRow icon={Mail} label="Email Address" value={user.email} />
                                    <InfoRow icon={Phone} label="Phone Number" value={user.phoneNumber} />
                                    <InfoRow icon={Calendar} label="Joined On" value={new Date(user.createdAt).toLocaleDateString()} />
                                    <InfoRow icon={Clock} label="Last Login" value={timeAgo(user.lastLogin)} />
                                    <InfoRow icon={CheckCircle} label="Verified" value={user.isVerified ? 'Yes' : 'No'} />
                                </div>
                            </div>

                            {/* Right Column - Device Information */}
                            <div>
                                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                    <Smartphone size={16} className="text-orange-500" />
                                    Device Information
                                </h3>
                                <div className="bg-gray-50 rounded-xl border border-gray-200 divide-y divide-gray-100">
                                    <InfoRow icon={Monitor} label="Device Name" value={user.deviceName} />
                                    <InfoRow icon={Smartphone} label="Device Type" value={user.deviceType} />
                                    <InfoRow icon={Shield} label="Connection Code" value={user.deviceConnectionCode} />
                                    <InfoRow icon={MapPin} label="Location" value={
                                        user.location?.coordinates
                                            ? `${user.location.coordinates[0]}, ${user.location.coordinates[1]}`
                                            : 'Not available'
                                    } />
                                    <InfoRow icon={CheckCircle} label="Registration Status" value={user.isRegistered ? 'Registered' : 'Not Registered'} />
                                    <InfoRow icon={Clock} label="Last Updated" value={new Date(user.updatedAt).toLocaleString()} />
                                </div>
                            </div>
                        </div>

                        {/* Media Attachments */}
                        {(user.signatureImage || user.videoAuth) && (
                            <div className="mt-6">
                                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                    <FileText size={16} className="text-orange-500" />
                                    Attachments
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {user.signatureImage && (
                                        <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                                                    <Image size={20} className="text-orange-500" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-gray-700">Signature</p>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            openMedia({
                                                                type: 'image',
                                                                url: user.signatureImage,
                                                                title: `Signature_${user.name || 'User'}`,
                                                            })
                                                        }
                                                        className="text-xs text-orange-500 hover:underline"
                                                    >
                                                        View Image
                                                    </button>
                                                </div>
                                                <button
                                                    onClick={() => handleDownload(user.signatureImage, `Signature_${user.name || 'User'}`)}
                                                    className="p-1.5 rounded-lg hover:bg-orange-100 text-gray-500 hover:text-orange-500"
                                                    title="Download"
                                                >
                                                    <Download size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {user.videoAuth && (
                                        <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                                                    <Video size={20} className="text-orange-500" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-gray-700">Video Authentication</p>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            openMedia({
                                                                type: 'video',
                                                                url: user.videoAuth,
                                                                title: `Video_Authentication_${user.name || 'User'}`,
                                                            })
                                                        }
                                                        className="text-xs text-orange-500 hover:underline"
                                                    >
                                                        Play Video
                                                    </button>
                                                </div>
                                                <button
                                                    onClick={() => handleDownload(user.videoAuth, `Video_Authentication_${user.name || 'User'}`)}
                                                    className="p-1.5 rounded-lg hover:bg-orange-100 text-gray-500 hover:text-orange-500"
                                                    title="Download"
                                                >
                                                    <Download size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Permissions */}
                        {user.permissions && user.permissions.length > 0 && (
                            <div className="mt-6">
                                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                    <Shield size={16} className="text-orange-500" />
                                    Permissions
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {user.permissions.map((perm, index) => (
                                        <PermissionBadge key={index} name={perm.name} enabled={perm.enabled} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Inline preview modal for signature image / video auth */}
            <MediaModal media={activeMedia} onClose={closeMedia} />
        </div>
    );
};

export default SingleUser;